@if (isAnyBannerVisible()) {
  @if (showRegistrationBanner() && !isDemoMode && !showCreditsBanner() && !showSubscriptionBanner()) {
    <div class="credits-banner row alert alert-warning d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center col-sm-9 col-7">
        <app-feather-icon [icon]="'alert-triangle'" class="me-2"></app-feather-icon>
        <span>
          <strong>{{ 'banner.completeRegistration' | transloco }}</strong>
          {{ 'banner.selectPlanHint' | transloco }}
        </span>
      </div>
      <button class="btn btn-warning col-auto" (click)="goToSubscriptionSettings()">
        {{ 'banner.selectPlan' | transloco }}
      </button>
    </div>
  }

  @if (showReactivationBanner() && !isDemoMode) {
    <div
      class="reactivation-banner alert d-flex flex-column flex-md-row flex-md-nowrap align-items-start align-items-md-center justify-content-md-between gap-2 gap-md-3"
    >
      <div class="d-flex align-items-center reactivation-banner__message">
        <!-- Bootstrap bi-gift (not app-feather-icon) so this matches the in-page
             reactivation block's icon exactly — both are the same font-glyph,
             colour and size, and a font-icon is cleanly styleable here whereas
             feather's innerHTML SVG is constrained to 14px by _alert.scss. (r70y) -->
        <i class="bi bi-gift me-2 flex-shrink-0"></i>
        <span>
          {{ 'banner.welcomeBack' | transloco }}
          <strong>{{ reactivationOffer()?.description || ('banner.monthsFree' | transloco) }}</strong>
          {{ 'banner.onSubscription' | transloco }}
        </span>
      </div>
      <!-- Desktop (md+): the row stays nowrap and flex-shrink-0 keeps both
           actions on the right while the message text wraps. Mobile (<md): the
           parent is flex-column so the actions sit BELOW the full-width message
           (flex-wrap lets the two buttons stack if the screen is very narrow)
           instead of crushing the text into a one-word column. -->
      <div class="d-flex gap-2 flex-shrink-0 flex-wrap">
        <!-- Primary = quick one-click reactivation on the PREVIOUS plan; the
             secondary routes to the Settings picker to choose a different tier.
             Both paths are explicit so the one-click claim never reads as
             "bypassing" the choose-any-plan flow. -->
        <button class="btn btn-reactivation" (click)="claimReactivationOffer()" [disabled]="isClaimingOffer">
          {{ isClaimingOffer ? ('banner.processing' | transloco) : ('banner.reactivatePreviousPlan' | transloco) }}
        </button>
        <button class="btn btn-reactivation-dismiss" (click)="goToSubscriptionSettings()" [disabled]="isClaimingOffer">
          {{ 'banner.chooseAPlan' | transloco }}
        </button>
      </div>
    </div>
  }

  @if (showSubscriptionBanner() && !showReactivationBanner()) {
    @let config = subscriptionBannerConfig();
    @if (config) {
      <div
        class="subscription-banner row alert d-flex align-items-center justify-content-between {{ config.alertClass }}"
      >
        <div class="d-flex align-items-center col-sm-9 col-7">
          <app-feather-icon [icon]="'alert-triangle'" class="me-2"></app-feather-icon>
          <span>{{ config.message }}</span>
        </div>
        <button class="btn btn-primary col-auto" (click)="handleCtaAction(config.ctaText)">
          {{ config.ctaText }}
        </button>
      </div>
    }
  }

  @if (
    showAuthExpiredBanner() && !isDemoMode && !isAgencyLevel() && !showSubscriptionBanner() && !showReactivationBanner()
  ) {
    <div class="auth-expired-banner row alert alert-warning d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center col-sm-9 col-7">
        <app-feather-icon [icon]="'alert-triangle'" class="me-2"></app-feather-icon>
        <span>
          <strong>{{ 'banner.dataSyncPaused' | transloco }}</strong>
          {{ 'banner.reconnectHint' | transloco }}
        </span>
      </div>
      <button class="btn btn-warning col-auto" (click)="reconnectShop()">{{ 'banner.reconnect' | transloco }}</button>
    </div>
  }

  @if (showDataImportBanner() && !isDemoMode && !showCreditsBanner() && !showSubscriptionBanner()) {
    <div class="import-banner row alert alert-info d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center" [ngClass]="showProgressButton ? 'col-sm-9 col-7' : 'col-12'">
        <app-feather-icon [icon]="'download-cloud'" class="me-2"></app-feather-icon>
        <span>
          <strong>{{ 'banner.importInProgress' | transloco }}</strong>
          {{ 'banner.importHint' | transloco }}
        </span>
      </div>
      @if (showProgressButton) {
        <button class="btn btn-primary col-auto" (click)="showImportProgress()">
          {{ 'banner.showProgress' | transloco }}
        </button>
      }
    </div>
  }

  @if (showCreditsBanner() && !isDemoMode) {
    <div class="credits-banner row alert alert-warning d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center col-sm-9 col-7">
        <app-feather-icon [icon]="'alert-triangle'" class="me-2"></app-feather-icon>
        <span>
          <strong>{{ 'banner.outOfCredits' | transloco }}</strong>
          {{ 'banner.creditsPaused' | transloco }}
        </span>
      </div>
      <button class="btn col-auto" [routerLink]="['/settings']" [queryParams]="{ section: 'subscriptions' }">
        {{ 'banner.addCredits' | transloco }}
      </button>
    </div>
  }

  @if (isDemoMode) {
    <!--
      Bootstrap's `.row` defaults to `flex-wrap: wrap`, which kicks the demo
      action buttons onto a second line whenever the i18n text + two buttons
      exceed the viewport. `flex-nowrap` keeps them on one row; the SCSS pairs
      this with `min-width: 0` on the text block + `flex-shrink: 0` on the
      buttons so the text truncates instead of the buttons wrapping.
    -->
    <div class="demo-banner alert d-flex flex-nowrap align-items-center justify-content-between gap-2">
      <div class="d-flex align-items-center demo-banner__message">
        <app-feather-icon [icon]="'check-circle'" class="me-2 flex-shrink-0"></app-feather-icon>
        <span>
          <strong>{{ 'banner.demoMode' | transloco }}</strong>
          {{ 'banner.demoTrial' | transloco }}
          <b class="btn-link" (click)="exitAndSignUp()">{{ 'banner.signUpNow' | transloco }}</b>
        </span>
      </div>
      <div class="d-flex align-items-center gap-2 demo-banner__actions">
        <!--
          Outline switch button — toggles between brand demo and agency demo
          without leaving demo mode. Label tracks `currentDemoType` so the user
          always sees the "go to the other one" verb.
        -->
        <button class="btn demo-switch-btn" (click)="switchDemoType()">
          @if (currentDemoType === 'agency') {
            {{ 'banner.switchToBrandDemo' | transloco }}
          } @else {
            {{ 'banner.switchToAgencyDemo' | transloco }}
          }
        </button>
        <button class="btn demo-btn" (click)="exitDemoMode()">
          {{ 'banner.exitDemo' | transloco }}
        </button>
      </div>
    </div>
  }
}
